// =============================================================================
// BP-05-03: Calendar Frontend - ReminderSettings Component
// =============================================================================

import React from 'react';
import { Bell, X, Plus } from 'lucide-react';
import { REMINDER_PRESETS } from '../types';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

interface ReminderSettingsProps {
  value: number[];
  onChange: (value: number[]) => void;
  className?: string;
}

// -----------------------------------------------------------------------------
// Utilities
// -----------------------------------------------------------------------------

function formatReminderTime(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} before`;
  }
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours === 1 ? '' : 's'} before`;
  }
  if (minutes < 10080) {
    const days = Math.floor(minutes / 1440);
    return `${days} day${days === 1 ? '' : 's'} before`;
  }
  const weeks = Math.floor(minutes / 10080);
  return `${weeks} week${weeks === 1 ? '' : 's'} before`;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ReminderSettings({
  value,
  onChange,
  className,
}: ReminderSettingsProps) {
  const addReminder = (minutes: number) => {
    if (!value.includes(minutes)) {
      onChange([...value, minutes].sort((a, b) => a - b));
    }
  };

  const removeReminder = (minutes: number) => {
    onChange(value.filter((v) => v !== minutes));
  };

  const availablePresets = REMINDER_PRESETS.filter(
    (preset) => !value.includes(preset.minutes)
  );

  return (
    <div className={cn('space-y-3', className)}>
      {/* Active Reminders */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((minutes) => (
            <div
              key={minutes}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-sage/10 text-sage-dark rounded-full text-sm"
            >
              <Bell className="w-3.5 h-3.5" />
              <span>{formatReminderTime(minutes)}</span>
              <button
                type="button"
                onClick={() => removeReminder(minutes)}
                className="p-0.5 hover:bg-sage/20 rounded-full transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Reminder */}
      {availablePresets.length > 0 && (
        <div className="relative">
          <select
            value=""
            onChange={(e) => {
              const minutes = parseInt(e.target.value, 10);
              if (!isNaN(minutes)) {
                addReminder(minutes);
              }
            }}
            className="w-full px-4 py-2 bg-cream border-2 border-transparent rounded-lg text-sm text-navy focus:outline-none focus:border-sage appearance-none cursor-pointer"
          >
            <option value="" disabled>
              Add reminder...
            </option>
            {availablePresets.map((preset) => (
              <option key={preset.minutes} value={preset.minutes}>
                {preset.label}
              </option>
            ))}
          </select>
          <Plus className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray pointer-events-none" />
        </div>
      )}

      {/* No Reminders */}
      {value.length === 0 && (
        <p className="text-sm text-warm-gray">No reminders set</p>
      )}
    </div>
  );
}

export default ReminderSettings;
